import { useQuery } from "@tanstack/react-query";
import { useAuthReady } from "hooks/useAuthReady";
import { request } from "hooks/useOkoApi/useOkoApi";

// The quotes the live tagger flagged during the session.
//
// Critical Incidents is built from these rather than from the LLM's
// `codedTranscriptData`: the tagger's tags are the
// deterministic ones, and a section that quotes a student to their teacher has
// to carry the words actually said.
//
// The endpoint returns every tagged quote, oldest first, with names already
// resolved — a flagged student may have answered nothing all session, so no
// other payload the report holds would name them.

const EMPTY = [];

/**
 * @param {string|null|undefined} sessionId
 * @param {boolean} enabled Skip the request for sessions outside the gate.
 * @param {string|null} educatorId Scope the read to the educator who ran the
 *   session. The endpoint answers for its owner, so an admin reading someone
 *   else's session names them; teachers reading their own pass nothing.
 * @returns {Array<{studentName: string, qNum: number|null, quote: string, tags: string[]}>}
 */
export const useTaggedQuotes = (sessionId, enabled = true, educatorId = null) => {
  const authReady = useAuthReady();

  const { data } = useQuery({
    queryKey: ["session-tagged-quotes", sessionId, educatorId],
    queryFn: () =>
      request(`account/session_history_tagged_quotes/${sessionId}${educatorId ? `?educator_id=${educatorId}` : ""}`, { method: "GET" }),
    enabled: Boolean(sessionId) && enabled && authReady,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  return toTaggedQuotes(data);
};

/** The endpoint's payload in the shape the report and the live pill read. */
export const toTaggedQuotes = (data) => {
  const quotes = data?.quotes;
  if (!Array.isArray(quotes) || quotes.length === 0) return EMPTY;

  return quotes.map(({ id: quoteId, student_name: name, student_id: id, question_number: qNum, quote, quote_timestamp: quoteTimestamp, tags }) => ({
    id: quoteId ?? null,
    quoteId: quoteId ?? null,
    studentName: name || id || "Unknown",
    qNum: qNum ?? null,
    quote,
    // Kept under its wire name: the transcript keys highlights on
    // `${quote_timestamp}|${quote}`.
    quote_timestamp: quoteTimestamp ?? null,
    tags: Array.isArray(tags) ? tags : [],
  }));
};
